import { Fragment, useEffect, useState, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import {
  FaChild, FaPlus, FaPencilAlt, FaSearch, FaBan, FaCheck,
  FaChevronDown, FaChevronRight, FaTimes, FaChalkboardTeacher, FaUserGraduate,
} from "react-icons/fa";
import Modal from "../Modal";

const API_DOMINICAL = "/api/dominical";
const API_CATALOGO = "/api/catalogos/clases_dominical";
const API_MIEMBROS = "/api/miembros";

export default function ClasesDominicalAdmin({ onBack }) {
  const [clases, setClases] = useState([]);
  const [miembros, setMiembros] = useState([]);
  const [loading, setLoading] = useState(false);

  const [busqueda, setBusqueda] = useState("");
  const [estatusFiltro, setEstatusFiltro] = useState("todos");
  const [expandidoMaestros, setExpandidoMaestros] = useState(null);
  const [expandidoAlumnos, setExpandidoAlumnos] = useState(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [claseEditando, setClaseEditando] = useState(null);
  const [form, setForm] = useState({ nombre: "", orden: "", edad_min: "", edad_max: "" });

  const [toast, setToast] = useState({ mostrar: false, mensaje: "", tipo: "success" });

  const mostrarToast = (mensaje, tipo = "success") => {
    setToast({ mostrar: true, mensaje, tipo });
    setTimeout(() => setToast({ mostrar: false, mensaje: "", tipo: "success" }), 3000);
  };

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const [resClases, resMiembros] = await Promise.all([
        fetch(`${API_DOMINICAL}/clases-detalle`, { credentials: "include" }),
        fetch(API_MIEMBROS, { credentials: "include" }),
      ]);
      setClases(await resClases.json());
      setMiembros(await resMiembros.json());
    } catch (error) {
      console.error(error);
      mostrarToast("Error cargando clases", "error");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    cargar();
  }, [cargar]);

  // ----------------------
  // CRUD de la clase
  // ----------------------
  const abrirNuevo = () => {
    setClaseEditando(null);
    setForm({ nombre: "", orden: "", edad_min: "", edad_max: "" });
    setModalOpen(true);
  };

  const abrirEditar = (clase) => {
    setClaseEditando(clase);
    setForm({
      nombre: clase.nombre,
      orden: clase.orden ?? "",
      edad_min: clase.edad_min ?? "",
      edad_max: clase.edad_max ?? "",
    });
    setModalOpen(true);
  };

  const guardarClase = async () => {
    if (!form.nombre.trim()) {
      mostrarToast("El nombre de la clase es obligatorio", "error");
      return;
    }

    const url = claseEditando ? `${API_CATALOGO}/${claseEditando.id}` : API_CATALOGO;
    const method = claseEditando ? "PUT" : "POST";

    try {
      const res = await fetch(url, {
        method,
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          nombre: form.nombre,
          orden: form.orden || null,
          edad_min: form.edad_min || null,
          edad_max: form.edad_max || null,
        }),
      });

      if (!res.ok) {
        mostrarToast("Error guardando la clase", "error");
        return;
      }

      mostrarToast(claseEditando ? "Clase actualizada" : "Clase creada");
      setModalOpen(false);
      cargar();
    } catch (error) {
      console.error(error);
      mostrarToast("Error inesperado", "error");
    }
  };

  const cambiarEstadoClase = async (clase) => {
    const activar = !clase.activo;
    try {
      const res = await fetch(`${API_CATALOGO}/${clase.id}`, {
        method: activar ? "PATCH" : "DELETE",
        credentials: "include",
      });

      if (!res.ok) {
        mostrarToast("Error actualizando el estatus", "error");
        return;
      }

      mostrarToast(activar ? "Clase reactivada" : "Clase desactivada");
      cargar();
    } catch (error) {
      console.error(error);
      mostrarToast("Error inesperado", "error");
    }
  };

  // ----------------------
  // Maestros / Alumnos por clase
  // ----------------------
  const quitarPersona = async (idPersonaDominical) => {
    try {
      const res = await fetch(`${API_DOMINICAL}/persona/${idPersonaDominical}`, {
        method: "DELETE",
        credentials: "include",
      });

      if (!res.ok) {
        mostrarToast("Error quitando de la clase", "error");
        return;
      }

      mostrarToast("Quitado de la clase");
      cargar();
    } catch (error) {
      console.error(error);
      mostrarToast("Error inesperado", "error");
    }
  };

  const agregarPersona = async (claseId, idMiembro, tipo) => {
    if (!idMiembro) return;
    try {
      const res = await fetch(`${API_DOMINICAL}/personas-clase`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ clase_id: claseId, id_miembro: idMiembro, tipo }),
      });

      const data = await res.json();

      if (!res.ok) {
        mostrarToast(data.error || "Error asignando a la clase", "error");
        return;
      }

      mostrarToast(tipo === 1 ? "Maestro agregado a la clase" : "Alumno agregado a la clase");
      cargar();
    } catch (error) {
      console.error(error);
      mostrarToast("Error inesperado", "error");
    }
  };

  // Miembros ya inscritos como alumno en cualquier clase (para no dejarlos elegir en 2 a la vez)
  const alumnosAsignadosGlobal = useMemo(() => {
    const set = new Set();
    clases.forEach((c) => c.alumnos.forEach((a) => a.id_miembro && set.add(a.id_miembro)));
    return set;
  }, [clases]);

  // ----------------------
  // Filtro de clases (cliente, catálogo pequeño)
  // ----------------------
  const clasesFiltradas = useMemo(() => {
    const texto = busqueda.trim().toLowerCase();

    return clases.filter((c) => {
      if (estatusFiltro === "activos" && !c.activo) return false;
      if (estatusFiltro === "inactivos" && c.activo) return false;
      if (texto && !c.nombre.toLowerCase().includes(texto)) return false;
      return true;
    });
  }, [clases, busqueda, estatusFiltro]);

  const rangoEdad = (clase) => {
    if (clase.edad_min == null && clase.edad_max == null) return "—";
    if (clase.edad_min != null && clase.edad_max != null) return `${clase.edad_min} - ${clase.edad_max} años`;
    if (clase.edad_min != null) return `Desde ${clase.edad_min} años`;
    return `Hasta ${clase.edad_max} años`;
  };

  return (
    <div className="p-6 bg-slate-50">

      {/* BREADCRUMB */}
      <div className="text-sm text-slate-400 mb-3">
        <Link to="/dashboard/administracion" className="hover:text-indigo-600">Administración</Link>
        <span className="mx-1.5">/</span>
        <button onClick={onBack} className="hover:text-indigo-600">Catálogos</button>
        <span className="mx-1.5">/</span>
        <span className="text-slate-600 font-medium">Clases Dominicales</span>
      </div>

      {/* HEADER */}
      <div className="flex flex-wrap justify-between items-start gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Clases Dominicales</h1>
          <p className="text-sm text-slate-500 mt-0.5">Clases de Escuela Dominical, su rango de edad, maestros y alumnos.</p>
        </div>

        <button
          onClick={abrirNuevo}
          className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium px-4 py-2 rounded-lg transition"
        >
          <FaPlus className="text-xs" /> Nueva clase
        </button>
      </div>

      {toast.mostrar && (
        <div className={`fixed top-5 right-5 z-[70] px-4 py-3 rounded-lg shadow-lg text-sm text-white ${toast.tipo === "success" ? "bg-emerald-600" : "bg-rose-600"}`}>
          {toast.mensaje}
        </div>
      )}

      {/* FILTROS */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 mb-4 flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[220px]">
          <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-300 text-sm" />
          <input
            placeholder="Buscar por nombre..."
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400"
          />
        </div>

        <div>
          <label className="text-xs text-slate-500 mb-1 block">Estatus</label>
          <select
            value={estatusFiltro}
            onChange={(e) => setEstatusFiltro(e.target.value)}
            className="border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400"
          >
            <option value="todos">Todos</option>
            <option value="activos">Activos</option>
            <option value="inactivos">Inactivos</option>
          </select>
        </div>
      </div>

      {/* TABLA */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-500">
              <tr>
                <th className="px-4 py-3 text-left font-semibold">Clase</th>
                <th className="px-4 py-3 text-left font-semibold">Orden</th>
                <th className="px-4 py-3 text-left font-semibold">Rango de edad</th>
                <th className="px-4 py-3 text-left font-semibold">Maestros</th>
                <th className="px-4 py-3 text-left font-semibold">Alumnos</th>
                <th className="px-4 py-3 text-left font-semibold">Estatus</th>
                <th className="px-4 py-3 text-right font-semibold">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7} className="text-center py-10 text-slate-400">Cargando...</td>
                </tr>
              ) : clasesFiltradas.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center py-10 text-slate-400">Sin resultados</td>
                </tr>
              ) : (
                clasesFiltradas.map((c) => {
                  const abiertoMaestros = expandidoMaestros === c.id;
                  const abiertoAlumnos = expandidoAlumnos === c.id;

                  const maestrosPendientes = miembros.filter(
                    (m) => !c.maestros.some((cm) => cm.id_miembro === m.id_miembro)
                  );
                  const alumnosPendientes = miembros.filter(
                    (m) => !c.alumnos.some((ca) => ca.id_miembro === m.id_miembro) && !alumnosAsignadosGlobal.has(m.id_miembro)
                  );

                  return (
                    <Fragment key={c.id}>
                      <tr className="border-t border-slate-100 hover:bg-slate-50 transition">
                        <td className="px-4 py-3">
                          <span className="flex items-center gap-2 font-medium text-slate-800">
                            <span className="w-7 h-7 rounded-md bg-indigo-50 text-indigo-600 flex items-center justify-center shrink-0">
                              <FaChild className="text-xs" />
                            </span>
                            {c.nombre}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-slate-600">{c.orden ?? "—"}</td>
                        <td className="px-4 py-3 text-slate-600">{rangoEdad(c)}</td>
                        <td className="px-4 py-3">
                          <button
                            onClick={() => setExpandidoMaestros(abiertoMaestros ? null : c.id)}
                            className="flex items-center gap-1.5 bg-indigo-50 text-indigo-700 px-2.5 py-1 rounded-md text-xs font-medium hover:bg-indigo-100 transition"
                          >
                            {abiertoMaestros ? <FaChevronDown className="text-[10px]" /> : <FaChevronRight className="text-[10px]" />}
                            {c.maestros.length === 0
                              ? "Sin maestros"
                              : c.maestros.length === 1
                              ? c.maestros[0].nombre
                              : `${c.maestros[0].nombre} +${c.maestros.length - 1}`}
                          </button>
                        </td>
                        <td className="px-4 py-3">
                          <button
                            onClick={() => setExpandidoAlumnos(abiertoAlumnos ? null : c.id)}
                            className="flex items-center gap-1.5 bg-emerald-50 text-emerald-700 px-2.5 py-1 rounded-md text-xs font-medium hover:bg-emerald-100 transition"
                          >
                            {abiertoAlumnos ? <FaChevronDown className="text-[10px]" /> : <FaChevronRight className="text-[10px]" />}
                            {c.alumnos.length === 0
                              ? "Sin alumnos"
                              : c.alumnos.length === 1
                              ? c.alumnos[0].nombre
                              : `${c.alumnos[0].nombre} +${c.alumnos.length - 1}`}
                          </button>
                        </td>
                        <td className="px-4 py-3">
                          <span className="flex items-center gap-2 text-slate-600">
                            <span className={`w-2 h-2 rounded-full ${c.activo ? "bg-emerald-500" : "bg-slate-300"}`} />
                            {c.activo ? "Activo" : "Inactivo"}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => abrirEditar(c)}
                              title="Editar"
                              className="w-8 h-8 flex items-center justify-center rounded-md border border-slate-200 text-slate-500 hover:bg-indigo-50 hover:text-indigo-700 hover:border-indigo-200 transition"
                            >
                              <FaPencilAlt className="text-xs" />
                            </button>
                            {c.activo ? (
                              <button
                                onClick={() => cambiarEstadoClase(c)}
                                title="Desactivar"
                                className="w-8 h-8 flex items-center justify-center rounded-md border border-slate-200 text-slate-500 hover:bg-rose-50 hover:text-rose-600 hover:border-rose-200 transition"
                              >
                                <FaBan className="text-xs" />
                              </button>
                            ) : (
                              <button
                                onClick={() => cambiarEstadoClase(c)}
                                title="Reactivar"
                                className="w-8 h-8 flex items-center justify-center rounded-md border border-slate-200 text-slate-500 hover:bg-emerald-50 hover:text-emerald-600 hover:border-emerald-200 transition"
                              >
                                <FaCheck className="text-xs" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>

                      {abiertoMaestros && (
                        <tr className="bg-slate-50/70 border-t border-slate-100">
                          <td colSpan={7} className="px-4 py-4">
                            <p className="flex items-center gap-1.5 text-xs font-semibold text-slate-500 uppercase mb-2">
                              <FaChalkboardTeacher className="text-[10px]" /> Maestros asignados
                            </p>
                            <div className="flex gap-2 flex-wrap items-center">
                              {c.maestros.map((m) => (
                                <span key={m.id} className="flex items-center gap-1 bg-indigo-50 text-indigo-700 px-2 py-1 rounded-md text-xs font-medium">
                                  {m.nombre}
                                  <button onClick={() => quitarPersona(m.id)} className="text-indigo-400 hover:text-rose-600 font-bold leading-none" title="Quitar maestro">
                                    <FaTimes className="text-[10px]" />
                                  </button>
                                </span>
                              ))}
                              {c.maestros.length === 0 && (
                                <span className="text-xs text-slate-400">Esta clase no tiene maestros asignados.</span>
                              )}
                              {maestrosPendientes.length > 0 && (
                                <select
                                  className="text-xs px-2 py-1 border border-slate-200 rounded-md text-slate-600 bg-white"
                                  onChange={(e) => agregarPersona(c.id, e.target.value, 1)}
                                  value=""
                                >
                                  <option value="" disabled>+ Agregar maestro</option>
                                  {maestrosPendientes.map((m) => (
                                    <option key={m.id_miembro} value={m.id_miembro}>{m.nombre} {m.paterno} {m.materno}</option>
                                  ))}
                                </select>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}

                      {abiertoAlumnos && (
                        <tr className="bg-slate-50/70 border-t border-slate-100">
                          <td colSpan={7} className="px-4 py-4">
                            <p className="flex items-center gap-1.5 text-xs font-semibold text-slate-500 uppercase mb-2">
                              <FaUserGraduate className="text-[10px]" /> Alumnos asignados
                            </p>
                            <div className="flex gap-2 flex-wrap items-center">
                              {c.alumnos.map((a) => (
                                <span key={a.id} className="flex items-center gap-1 bg-emerald-50 text-emerald-700 px-2 py-1 rounded-md text-xs font-medium">
                                  {a.nombre}
                                  <button onClick={() => quitarPersona(a.id)} className="text-emerald-400 hover:text-rose-600 font-bold leading-none" title="Quitar alumno">
                                    <FaTimes className="text-[10px]" />
                                  </button>
                                </span>
                              ))}
                              {c.alumnos.length === 0 && (
                                <span className="text-xs text-slate-400">Esta clase no tiene alumnos asignados.</span>
                              )}
                              {alumnosPendientes.length > 0 && (
                                <select
                                  className="text-xs px-2 py-1 border border-slate-200 rounded-md text-slate-600 bg-white"
                                  onChange={(e) => agregarPersona(c.id, e.target.value, 2)}
                                  value=""
                                >
                                  <option value="" disabled>+ Agregar alumno</option>
                                  {alumnosPendientes.map((m) => (
                                    <option key={m.id_miembro} value={m.id_miembro}>{m.nombre} {m.paterno} {m.materno}</option>
                                  ))}
                                </select>
                              )}
                            </div>
                            <p className="text-[11px] text-slate-400 mt-2">
                              Un alumno solo puede pertenecer a una clase a la vez; los miembros ya inscritos en otra clase no aparecen en la lista.
                            </p>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      <p className="text-sm text-slate-500 mt-4">
        Mostrando {clasesFiltradas.length} de {clases.length} clases
      </p>

      {/* MODAL NUEVA / EDITAR CLASE */}
      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={claseEditando ? "Editar clase" : "Nueva clase"}
      >
        <div>
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">
                Clase <span className="text-rose-500">*</span>
              </label>
              <input
                type="text"
                value={form.nombre}
                onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                className="w-full border border-slate-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">Orden</label>
              <input
                type="number"
                value={form.orden}
                onChange={(e) => setForm({ ...form, orden: e.target.value })}
                className="w-full border border-slate-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">Edad mínima</label>
                <input
                  type="number"
                  min="0"
                  placeholder="Ej. 0"
                  value={form.edad_min}
                  onChange={(e) => setForm({ ...form, edad_min: e.target.value })}
                  className="w-full border border-slate-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">Edad máxima</label>
                <input
                  type="number"
                  min="0"
                  placeholder="Ej. 2"
                  value={form.edad_max}
                  onChange={(e) => setForm({ ...form, edad_max: e.target.value })}
                  className="w-full border border-slate-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400"
                />
              </div>
            </div>
            <p className="text-[11px] text-slate-400">Indica qué alumnos pueden ingresar a esta clase según su edad (opcional).</p>
          </div>

          <div className="flex justify-end gap-2 mt-4 pt-3 border-t border-slate-100">
            <button
              onClick={() => setModalOpen(false)}
              className="px-3.5 py-1.5 text-sm rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 transition"
            >
              Cancelar
            </button>
            <button
              onClick={guardarClase}
              className="px-3.5 py-1.5 text-sm rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white font-medium transition"
            >
              Guardar
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
